// ============================================================================
// The words on an Ask AI card's button — ONE copy, read by the card (the app,
// src/modules/copilot/*) and by Ask AI's reply (index.ts returns them with the
// draft), so when Ask AI says what to press it says the button's real words:
// "tap Merge into Dana Pierson", never "tap Do it" when the button says
// something else (item 944, card fixes; TCSD-0533 "D3. B").
//
// Pure: no imports, no Deno, no browser — vitest, Vite and Deno all load it.
// ============================================================================

/** The button's words when nothing names the consequence. */
export const DO_IT = "Do it";

const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));

/** "Oct 31, 2026" from an ISO date, or the words given. */
export function dayWords(iso: unknown): string {
  const s = str(iso);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s || "—";
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? s : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** A new-account card's stage in the screen's words (NEW_ACCOUNT_SOP_STAGE_LABELS). */
export function nasStageWords(stage: unknown): string {
  switch (stage) {
    case "Info_and_Sub_Checklist": return "Info & Sub Checklist";
    case "Centralized_Confirmation_Call": return "Centralized Confirmation Call";
    case "Executive_Review": return "Executive Review";
    case "Final_Pre_Invoicing_Check": return "Final Pre-Invoicing Check";
    case "Completed": return "Completed";
    case "Cancelled": return "Cancelled";
    case "Stalled": return "Stalled";
    default: return str(stage) || "—";
  }
}

/** A sub card's button words — the Subcontractors screens' own. */
export function subButtonWords(action: string, f: Record<string, unknown>): string | null {
  switch (action) {
    case "move_stage": return `Move to ${({ Lead: "Lead", Meet: "Interviewing", Ready: "Bench" } as Record<string, string>)[str(f.to)] ?? str(f.to)}`;
    case "start_paperwork": return "Start paperwork now";
    case "mark_inactive": return "Mark inactive";
    case "return_to_bench": return "Return to bench";
    case "set_tier_1": return f.on === false ? "Remove Tier 1" : "Make Tier 1";
    default: return null;
  }
}

/** A hiring card's button words — the Hiring pipeline's own (HIRING_STAGE_LABELS, the edit panel). */
export function hiringButtonWords(action: string, f: Record<string, unknown>): string | null {
  const stage: Record<string, string> = { Open: "Open", Interview: "Interview", Filled: "Filled · Hired", On_Hold: "On Hold", No_Status: "No Status" };
  switch (action) {
    case "move_stage": return `Move to ${stage[str(f.to)] ?? str(f.to)}`;
    case "add_opening": return "Add another opening like this";
    case "mark_not_needed": return "Mark as no longer needed";
    default: return null;
  }
}

/** An onboarding card's button words — the onboarding panel's own ("setup" on a sub's card). */
export function onboardingButtonWords(action: string, _f: Record<string, unknown>, kind: string): string | null {
  const word = kind === "W2" ? "onboarding" : "setup";
  switch (action) {
    case "move_stage": return "Move stage";
    case "set_checklist_item": return "Update checklist";
    case "add_note": return "Add note";
    case "archive": return `Archive ${word}`;
    case "restore": return `Restore ${word}`;
    default: return null;
  }
}

/** A My Work row's button words — the row's own (src/modules/home/row-actions.tsx, the history page). */
export function myWorkButtonWords(action: string, c: Record<string, unknown>): string | null {
  switch (action) {
    case "send_to": return `Send to ${str(c.to_name) || "them"}`;
    case "clear": return "Clear";
    case "reopen": return "Reopen";
    default: return null;
  }
}

/** A task card's button words — the task screen's own (task-detail.tsx, task-action-modals.tsx). */
export function taskButtonWords(action: string): string | null {
  switch (action) {
    case "add_comment": return "Add comment";
    case "resolve": return "Resolve";
    case "reopen": return "Reopen";
    case "reassign": return "Reassign";
    case "send_back": return "Send back";
    case "edit": return "Save changes";
    default: return null;
  }
}

/** The missed-shift card's button names what it records (item #982 demo, Ben approved 2026-10-26). */
export function timeButtonWords(input: Record<string, unknown>): string | null {
  const c = (input.context ?? {}) as Record<string, unknown>;
  const h = typeof c.hours === "number" ? c.hours : null;
  const who = typeof c.worker_name === "string" ? c.worker_name : null;
  return h != null && who ? `Record ${h.toFixed(2)} hours for ${who}` : "Record shift";
}

/** A new-account card's button words — the card screen's own (sop-detail.tsx). */
export function newAccountButtonWords(action: string, f: Record<string, unknown>): string | null {
  switch (action) {
    case "move_stage": return `Move to ${nasStageWords(f.to)}`;
    case "mark_stalled": return "Mark stalled";
    case "cancel_card": return "Cancel card";
    case "add_note": return "Add to notes";
    case "record_signoff": return f.kind === "exec_review" ? "Record executive review" : "Record confirmation call";
    case "delete_card": return "Delete";
    case "archive_card": return "Archive card";
    case "restore_card": return "Restore to board";
    default: return null;
  }
}

/**
 * A building card's button words (null = "Do it"). `here` is the building as
 * people know it, "Name (cleaner)"; `f` the card's fields; `c` the draft's context.
 */
export function buildingButtonWords(
  action: string,
  here: string,
  f: Record<string, unknown>,
  c: Record<string, unknown>,
): string | null {
  switch (action) {
    case "acknowledge_termination": return `Acknowledge for ${f.as === "finance" ? "Finance" : "HR"}`;
    case "approve_termination": return `Approve ending service at ${here}`;
    case "set_termination_final_date": return `Set the last clean to ${dayWords(f.final_service_date)}`;
    case "cancel_termination": return `Cancel the termination for ${here}`;
    case "merge_contacts": return `Merge into ${str(((c.target ?? {}) as Record<string, unknown>).name)}`;
    default: return null;
  }
}

/** Dollars as the screens show them: $1,240.00. */
export function moneyWords(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
}

/** The project screen's own button words for each move (project-detail.tsx). */
const PROJECT_MOVE_WORDS: Record<string, string> = {
  Ready_to_Invoice: "Mark Ready to Invoice",
  Pending_PO: "Send to Pending PO",
  Invoiced: "Mark Invoiced",
  Completed: "Mark Completed",
};

/**
 * A project card's button words — the project screen's own buttons, or for a
 * card that moves money or cannot be undone, the consequence (TCSD-0533).
 */
export function projectButtonWords(action: string, f: Record<string, unknown>, c: Record<string, unknown>): string | null {
  switch (action) {
    case "repeat_project": return "Repeat this one";
    case "move_stage": return PROJECT_MOVE_WORDS[str(f.to)] ?? null;
    case "send_back_to_am": return "Send back to AM";
    case "resubmit": return "Mark addressed → Ready to Invoice";
    case "pay_line": {
      const line = (c.line ?? {}) as Record<string, unknown>;
      return `Pay ${str(line.provider_name) || "the sub"} ${moneyWords(line.amount)}`;
    }
    case "delete_project": return `Delete ${str(c.label) || "this project"}`;
    default: return null;
  }
}

/**
 * What the button on a just-drafted card says, from the draft as it was saved.
 * Used by the server so the reply names the real button.
 */
export function buttonWordsFor(toolName: string, input: Record<string, unknown>): string {
  if (toolName === "propose_building_action") {
    const here = str(input.display_name) || str(input.facility_name) || "this building";
    return buildingButtonWords(
      str(input.action),
      here,
      (input.fields ?? {}) as Record<string, unknown>,
      (input.context ?? {}) as Record<string, unknown>,
    ) ?? DO_IT;
  }
  if (toolName === "propose_task_action") return taskButtonWords(str(input.action)) ?? DO_IT;
  if (toolName === "propose_time_action") return timeButtonWords(input) ?? DO_IT;
  if (toolName === "propose_sub_action") return subButtonWords(str(input.action), (input.fields ?? {}) as Record<string, unknown>) ?? DO_IT;
  if (toolName === "propose_mileage") return "Save reading";
  if (toolName === "propose_separation_item") return str(input.action) === "assign" ? "Hand it on" : "Mark done";
  if (toolName === "propose_machine_action") return ({ move: "Move", confirm_here: "Yes, still here", set_status: "Save" } as Record<string, string>)[str(input.action)] ?? DO_IT;
  if (toolName === "propose_supply_action") return str(input.action) === "reject" ? "Reject" : "Approve";
  if (toolName === "propose_onboarding_action") return onboardingButtonWords(str(input.action), (input.fields ?? {}) as Record<string, unknown>, str(((input.context ?? {}) as Record<string, unknown>).kind)) ?? DO_IT;
  if (toolName === "propose_hiring_action") return hiringButtonWords(str(input.action), (input.fields ?? {}) as Record<string, unknown>) ?? DO_IT;
  if (toolName === "propose_my_work_action") return myWorkButtonWords(str(input.action), (input.context ?? {}) as Record<string, unknown>) ?? DO_IT;
  if (toolName === "propose_new_account_action") {
    return newAccountButtonWords(str(input.action), (input.fields ?? {}) as Record<string, unknown>) ?? DO_IT;
  }
  if (toolName === "propose_project_action") {
    return projectButtonWords(
      str(input.action),
      (input.fields ?? {}) as Record<string, unknown>,
      (input.context ?? {}) as Record<string, unknown>,
    ) ?? DO_IT;
  }
  return DO_IT;
}
